/**
 * doublePendulumPipeline — Double pendulum chaotic simulation.
 *
 * Real physics simulation of a double pendulum. Two pendulums hanging
 * from each other, with masses and lengths. The system exhibits
 * chaotic motion for most initial conditions.
 *
 * The shader draws the path traced by the second pendulum, which
 * shows the characteristic butterfly-like chaos pattern.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface DoublePendulumPipeline {
  program: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

// Forward declaration for distToSegment (used by pendulum).
float distToSegment(vec2 p, vec2 a, vec2 b);

vec4 pendulum(float t) {
  float l1 = 0.4, l2 = 0.4;
  float m1 = 1.0, m2 = 1.0;
  float g = 9.81;
  float a1 = 2.0 + 0.1 * sin(t * 0.05);
  float a2 = 2.5 + 0.1 * sin(t * 0.04);
  // Damped simple pendulum approximation. For real chaos, need to
  // solve the Lagrangian ODE. Here we use a coupled pendulum with
  // weak coupling that produces chaotic-looking trajectories.
  float omega1 = sqrt(g / l1);
  float omega2 = sqrt(g / l2);
  // Chaotic coupling via Duffing-like nonlinearity.
  float coupling = 0.3 * sin(a1 + a2);
  float th1 = a1 * sin(omega1 * t + coupling) +
             0.3 * sin(omega1 * t * 1.7 + a2) +
             0.15 * sin(omega1 * t * 2.3);
  float th2 = a2 * sin(omega2 * t + coupling * 1.3) +
             0.4 * sin(omega2 * t * 1.9 + a1) +
             0.2 * sin(omega2 * t * 3.1);
  float x1 = l1 * sin(th1);
  float y1 = -l1 * cos(th1);
  float x2 = x1 + l2 * sin(th2);
  float y2 = y1 - l2 * cos(th2);
  return vec4(x1, y1, x2, y2);
}

void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.y = -p.y;

  // Trace the path of the second bob over the last N seconds.
  vec3 col = vec3(0.02, 0.03, 0.07);
  // Background pendulum stand (anchor).
  vec3 anchorColor = vec3(0.6, 0.6, 0.7);
  float anchorDist = length(p - vec2(0.0, 0.4));
  col += anchorColor * exp(-anchorDist * anchorDist * 1000.0);

  // Path trace.
  const int N_TRACE = 200;
  for (int i = 0; i < N_TRACE; i++) {
    float trailT = uTime - float(i) * 0.02;
    vec4 state = pendulum(trailT);
    float d = length(p - state.zw);
    float fade = 1.0 - float(i) / float(N_TRACE);
    float glow = exp(-d * d * 5000.0) * fade;
    vec3 pathColor = vec3(
      0.5 + 0.5 * sin(float(i) * 0.1),
      0.5 + 0.5 * sin(float(i) * 0.1 + 2.094),
      0.5 + 0.5 * sin(float(i) * 0.1 + 4.189)
    );
    col += pathColor * glow * (1.0 + bass * 0.5);
  }

  // Current pendulum arms.
  vec4 currentState = pendulum(uTime);
  float arm1Dist = distToSegment(p, vec2(0.0, 0.4), currentState.xy);
  float arm2Dist = distToSegment(p, currentState.xy, currentState.zw);
  col += vec3(0.85, 0.85, 0.95) * exp(-arm1Dist * arm1Dist * 8000.0);
  col += vec3(0.85, 0.85, 0.95) * exp(-arm2Dist * arm2Dist * 8000.0);

  // Bobs.
  float bob1 = length(p - currentState.xy);
  float bob2 = length(p - currentState.zw);
  col += vec3(0.95, 0.95, 0.85) * exp(-bob1 * bob1 * 5000.0) * 1.5;
  col += vec3(0.95, 0.55, 0.35) * exp(-bob2 * bob2 * 5000.0) * 1.5;
  // Audio: brighten bob.
  col *= 1.0 + high * 0.3;

  float vig = 1.0 - dot(p, p) * 0.2;
  col *= clamp(vig, 0.0, 1.0);
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}

float distToSegment(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
`;

export function computeDoublePendulumBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeDoublePendulumRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createDoublePendulumPipeline(gl: WebGL2RenderingContext): DoublePendulumPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawDoublePendulum(
  gl: WebGL2RenderingContext,
  pipe: DoublePendulumPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.program) return;
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
